import { Navigation } from '@/components/Navigation';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, Users, Calendar } from 'lucide-react';
import { categories } from '@/utils/categories';

const Home = () => {
  const heroImages = [
    'https://images.unsplash.com/photo-1681149341674-45fd772fd463?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxjb25jZXJ0JTIwc2luZ2VyJTIwc3RhZ2UlMjBsaWdodHN8ZW58MHx8fHwxNzcyNjM4MDQxfDA&ixlib=rb-4.1.0&q=85',
    'https://images.unsplash.com/photo-1764201068108-b5ef6005f3c9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwzfHxjb25jZXJ0JTIwY3Jvd2QlMjBjaGVlcmluZyUyMG5pZ2h0fGVufDB8fHx8MTc3MjYzODA1OXww&ixlib=rb-4.1.0&q=85',
    'https://images.unsplash.com/photo-1761882619891-6529ff92df0a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwyfHxjb250ZW1wb3JhcnklMjBkYW5jZXIlMjBqdW1waW5nJTIwc3R1ZGlvfGVufDB8fHx8MTc3MjYzODA0Mnww&ixlib=rb-4.1.0&q=85',
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="home-page">
      <Navigation />

      <section className="min-h-[80vh] flex items-center px-6 md:px-12 lg:px-24 py-20">
        <div className="max-w-7xl mx-auto w-full">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8" data-testid="hero-content">
              <h1 className="text-5xl md:text-7xl font-semibold tracking-tight leading-[1.1]">
                Book the
                <br />
                <span className="gradient-text">Unforgettable</span>
              </h1>
              <p className="text-lg md:text-xl text-gray-600 leading-relaxed max-w-xl">
                Connect with top-tier artists, performers, and creators for your next event.
                From musicians to magicians, find the perfect talent.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/artists">
                  <Button
                    size="lg"
                    className="rounded-full bg-purple-600 hover:bg-purple-700 h-12 px-8 text-sm font-semibold tracking-wide uppercase shadow-lg shadow-purple-500/20"
                    data-testid="explore-artists-button"
                  >
                    Explore Artists
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/register">
                  <Button
                    size="lg"
                    variant="outline"
                    className="rounded-full h-12 px-8 border-gray-200 hover:border-gray-900"
                    data-testid="join-as-artist-button"
                  >
                    Join as Artist
                  </Button>
                </Link>
              </div>
            </div>

            <div className="relative" data-testid="hero-images">
              <div className="grid grid-cols-2 gap-4">
                {heroImages.map((img, idx) => (
                  <div
                    key={idx}
                    className={`rounded-2xl overflow-hidden ${idx === 0 ? 'col-span-2 h-64' : 'h-48'}`}
                    style={{ animationDelay: `${idx * 0.2}s` }}
                  >
                    <img
                      src={img}
                      alt="Artist performance"
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-24 py-20 bg-gray-50" data-testid="stats-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-12">
            <div className="text-center space-y-2">
              <Users className="w-12 h-12 mx-auto text-purple-600" />
              <div className="text-4xl font-bold text-gray-900">500+</div>
              <div className="text-gray-600">Verified Artists</div>
            </div>
            <div className="text-center space-y-2">
              <Calendar className="w-12 h-12 mx-auto text-purple-600" />
              <div className="text-4xl font-bold text-gray-900">1000+</div>
              <div className="text-gray-600">Events Completed</div>
            </div>
            <div className="text-center space-y-2">
              <Star className="w-12 h-12 mx-auto text-purple-600" />
              <div className="text-4xl font-bold text-gray-900">4.8/5</div>
              <div className="text-gray-600">Average Rating</div>
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-24 py-20" data-testid="categories-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-4xl md:text-5xl font-medium tracking-tight">Browse by Category</h2>
            <p className="text-lg text-gray-600">Find the perfect artist for any occasion</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {Object.values(categories).map((category) => (
              <Link
                key={category.value}
                to={`/artists?category=${category.value}`}
                className="group"
                data-testid={`category-card-${category.value}`}
              >
                <div className="overflow-hidden bg-white border border-gray-100 rounded-2xl hover-lift">
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={category.image}
                      alt={category.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 image-overlay"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <h3 className="text-xl font-semibold text-white">{category.name}</h3>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-24 py-20 bg-gradient-to-r from-purple-600 to-indigo-600" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 className="text-4xl md:text-5xl font-medium text-white tracking-tight">
            Ready to Make Your Event Memorable?
          </h2>
          <p className="text-xl text-purple-100">
            Join thousands of happy customers who found their perfect artist match
          </p>
          <Link to="/artists">
            <Button
              size="lg"
              className="rounded-full bg-white text-purple-600 hover:bg-gray-100 h-12 px-8"
              data-testid="get-started-cta-button"
            >
              Get Started Now
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>

      <footer className="bg-gray-900 text-white px-6 md:px-12 lg:px-24 py-12">
        <div className="max-w-7xl mx-auto text-center">
          <div className="text-2xl font-bold gradient-text mb-4">BOOK A STAR</div>
          <p className="text-gray-400">Connecting talent with opportunities</p>
          <p className="text-gray-500 text-sm mt-4">© 2026 BOOK A STAR. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Home;
